export const authStorageKey = 'school-erp-auth'
export const roleStorageKey = 'school-erp-role'
export const rawRoleStorageKey = 'school-erp-raw-role'

export type UserRole = 'admin' | 'teacher' | string

export function isOrganizationAdminRole(rawRole?: string | null): boolean {
  let roleToCheck = rawRole
  if (!roleToCheck && typeof window !== 'undefined') {
    roleToCheck = window.localStorage.getItem(rawRoleStorageKey)
    if (!roleToCheck) {
      try {
        const userMeta = window.localStorage.getItem('school-erp-user-meta')
        if (userMeta) {
          const parsed = JSON.parse(userMeta)
          roleToCheck = parsed.rawRole || parsed.userType || parsed.role
        }
      } catch {}
    }
  }
  if (!roleToCheck) return false
  const norm = roleToCheck.trim().toLowerCase()
  return norm === 'organizational_admin' || norm === 'org_admin'
}

const roleHomePaths: Record<string, string> = {
  admin: '/admin/dashboard',
  branch_admin: '/admin/dashboard',
  organizational_admin: '/organization-admin/dashboard',
  teacher: '/teacher/dashboard',
}

export function normalizeStoredRole(role: string | null): UserRole | null {
  if (!role) return null
  const normalized = role.trim().toLowerCase()
  if (
    normalized === 'admin' ||
    normalized === 'branch_admin' ||
    normalized === 'organizational_admin' ||
    normalized === 'superadmin' ||
    normalized === 'super_admin' ||
    normalized.includes('admin')
  ) {
    return 'admin'
  }
  if (
    normalized === 'teacher' ||
    normalized === 'class teacher' ||
    normalized.includes('teacher')
  ) {
    return 'teacher'
  }

  return (role.toLowerCase() as UserRole)
}

import { getAccessToken } from '../shared/utils/tokenStorage'

export function isAuthenticated() {
  const token = getAccessToken()
  const isAuthSession = window.localStorage.getItem(authStorageKey) === 'true'
  return Boolean(token && isAuthSession)
}

export function canAccessProtectedRoutes() {
  return isAuthenticated()
}

export function getRoleHomePath(role: string | null, permissions?: string[], rawRole?: string | null) {
  if (isOrganizationAdminRole(rawRole || role)) {
    return '/organization-admin/dashboard'
  }

  let userPerms = permissions || []
  if (!userPerms || userPerms.length === 0) {
    try {
      const stored = window.localStorage.getItem('school-erp-permissions')
      if (stored) userPerms = JSON.parse(stored)
    } catch {
      userPerms = []
    }
  }

  const normalizedRole = normalizeStoredRole(role)

  const hasExactPerm = (p: string) => {
    if (userPerms.includes(p)) return true
    const pDot = p.replace(/:/g, '.')
    const pColon = p.replace(/\./g, ':')
    return userPerms.includes(pDot) || userPerms.includes(pColon)
  }

  // 1. Admin role or global wildcard permission
  if (normalizedRole === 'admin' || userPerms.includes('*')) {
    return '/admin/dashboard'
  }

  const isTeachingStaff =
    normalizedRole === 'teacher' ||
    userPerms.includes('route:staff:access') ||
    userPerms.some((p) =>
      p.startsWith('assignments.') ||
      p.startsWith('assignments:') ||
      p.startsWith('lesson-plans.') ||
      p.startsWith('lesson-plans:') ||
      p.startsWith('timetable.') ||
      p.startsWith('timetable:') ||
      p.startsWith('attendance.') ||
      p.startsWith('attendance:')
    )

  const hasAdminOnlyModulePerm =
    hasExactPerm('finance.view') ||
    hasExactPerm('finance') ||
    hasExactPerm('user-management.view') ||
    hasExactPerm('users.view') ||
    hasExactPerm('roles.view') ||
    hasExactPerm('departments.view') ||
    hasExactPerm('designations.view') ||
    hasExactPerm('hr.view') ||
    hasExactPerm('hr') ||
    hasExactPerm('admissions.view') ||
    hasExactPerm('admissions')

  // 2. If user is teaching staff without admin-only modules, send directly to /teacher/dashboard
  if (isTeachingStaff && !hasAdminOnlyModulePerm) {
    return '/teacher/dashboard'
  }

  // 3. Permission-based Routing for specialized admin staff (Finance, HR, Admissions, User Management, etc.)
  if (hasExactPerm('finance.view') || hasExactPerm('finance')) {
    return '/admin/finance/fees-finance'
  }
  if (hasExactPerm('user-management.view') || hasExactPerm('users.view') || hasExactPerm('roles.view')) {
    return '/admin/user-management'
  }
  if (hasExactPerm('departments.view') || hasExactPerm('designations.view') || hasExactPerm('hr.view') || hasExactPerm('hr')) {
    return '/admin/hr-management'
  }
  if (hasExactPerm('admissions.view') || hasExactPerm('admissions')) {
    return '/admin/admission'
  }
  if (hasExactPerm('communication.view') || hasExactPerm('communications.view')) {
    return '/admin/communication'
  }
  if (hasExactPerm('academics.view') || hasExactPerm('class-management.view')) {
    return '/admin/academics'
  }

  // 4. Default for teaching staff
  if (isTeachingStaff) {
    return '/teacher/dashboard'
  }

  return '/admin/dashboard'
}

export function getStoredRoleHomePath() {
  const role = window.localStorage.getItem(roleStorageKey)
  const rawRole = window.localStorage.getItem(rawRoleStorageKey)
  let perms: string[] = []
  try {
    const stored = window.localStorage.getItem('school-erp-permissions')
    if (stored) perms = JSON.parse(stored)
  } catch {}
  return getRoleHomePath(role, perms, rawRole)
}
